import React from 'react';
import PropTypes from 'prop-types';
import SortSelection, { createSortSelection } from '../../common/SortSelection';
import { inject, observer } from 'mobx-react';
import { defineMessages } from 'react-intl';

import ProductSortOrderBy from '../../../types/ProductSortOrderBy';
import SortOrder from '../../../types/SortOrder';
import ConfigStore from '../../../store/ConfigStore';
import ProductStore from '../../../store/ProductStore';
import { modelOf } from '../../../prop-types';
import { findSortSelectionForDefaultOption } from '../../../util/mapDefaultSortOption';

const sortNameMessages = defineMessages({
  sortDefault: {
    id: 'sort.default',
    defaultMessage: 'Default',
  },
  sortRelevance: {
    id: 'sort.relevance',
    defaultMessage: 'Most relevant',
  },
  sortNewestOffers: {
    id: 'sort.newestOffers',
    defaultMessage: 'Newest offers',
  },
  sortRecommended: {
    id: 'sort.recommended',
    defaultMessage: 'Recommended',
  },
  sortCheapest: {
    id: 'sort.cheapest',
    defaultMessage: 'Cheapest',
  },
  sortMostExpensive: {
    id: 'sort.mostExpensive',
    defaultMessage: 'Most expensive',
  },
  sortLatest: {
    id: 'sort.latest',
    defaultMessage: 'Latest',
  },
  sortMostPopular: {
    id: 'sort.mostPopular',
    defaultMessage: 'Most popular',
  },
  sortBestRated: {
    id: 'sort.bestRated',
    defaultMessage: 'Best rated',
  },
  sortBrand: {
    id: 'sort.brand',
    defaultMessage: 'Brand',
  },
  sortId: {
    id: 'sort.id',
    defaultMessage: 'Product number',
  },
  sortAlphabetical: {
    id: 'sort.alphabetical',
    defaultMessage: 'Alphabetical',
  },
  sortCategory: {
    id: 'sort.category',
    defaultMessage: 'Product category',
  },
  sortDiscount: {
    id: 'sort.discount',
    defaultMessage: 'Best discount',
  },
  sortInStockQuantity: {
    id: 'sort.inStockQuantity',
    defaultMessage: 'Most in stock',
  },
});

const defaultSortFallback = createSortSelection(
  sortNameMessages.sortDefault,
  null,
  null
);

// Page-specific defaults: not in PRODUCT_LIST_SORT_SELECTORS indexes;
// force-included when listDefaultSortOption maps to them.
const relevanceSortSelection = createSortSelection(
  sortNameMessages.sortRelevance,
  ProductSortOrderBy.RELEVANCE,
  SortOrder.DESC
);

const campaignDateAddedSortSelection = createSortSelection(
  sortNameMessages.sortNewestOffers,
  ProductSortOrderBy.CAMPAIGN_DATE_ADDED,
  SortOrder.DESC
);

const recommendedSortSelection = createSortSelection(
  sortNameMessages.sortRecommended,
  ProductSortOrderBy.RECOMMENDED_SORT_ORDER,
  SortOrder.ASC
);

const pageSpecificSortSelections = [
  relevanceSortSelection,
  campaignDateAddedSortSelection,
  recommendedSortSelection,
];

const catalogSortSelections = [
  createSortSelection(
    sortNameMessages.sortCheapest,
    ProductSortOrderBy.PRICE,
    SortOrder.ASC,
    1
  ),
  createSortSelection(
    sortNameMessages.sortMostExpensive,
    ProductSortOrderBy.PRICE,
    SortOrder.DESC,
    2
  ),
  createSortSelection(
    sortNameMessages.sortLatest,
    ProductSortOrderBy.DATE_ADDED,
    SortOrder.DESC,
    3
  ),
  createSortSelection(
    sortNameMessages.sortMostPopular,
    ProductSortOrderBy.SALES,
    SortOrder.DESC,
    4
  ),
  createSortSelection(
    sortNameMessages.sortBestRated,
    ProductSortOrderBy.REVIEWS_AVERAGE,
    SortOrder.DESC,
    5
  ),
  createSortSelection(
    sortNameMessages.sortBrand,
    ProductSortOrderBy.BRAND,
    SortOrder.ASC,
    6
  ),
  createSortSelection(
    sortNameMessages.sortId,
    ProductSortOrderBy.ID,
    SortOrder.ASC,
    7
  ),
  createSortSelection(
    sortNameMessages.sortAlphabetical,
    ProductSortOrderBy.NAME,
    SortOrder.ASC,
    8
  ),
  createSortSelection(
    sortNameMessages.sortCategory,
    ProductSortOrderBy.CATEGORY,
    SortOrder.ASC,
    9
  ),
  createSortSelection(
    sortNameMessages.sortDiscount,
    ProductSortOrderBy.DISCOUNT,
    SortOrder.DESC,
    10
  ),
  createSortSelection(
    sortNameMessages.sortInStockQuantity,
    ProductSortOrderBy.IN_STOCK_QUANTITY,
    SortOrder.DESC,
    11
  ),
];

export const ProductListSortSelection = ({
  configStore,
  productStore,
  onSelectSort,
  selectedSortKey,
  selectedSortOrder,
  listDefaultSortOption,
}) => {
  const mappedDefaultSelection = findSortSelectionForDefaultOption(
    listDefaultSortOption,
    [...catalogSortSelections, ...pageSpecificSortSelections]
  );

  const filterActiveSortSelections = () => {
    const activeProductSortSelectors = productStore.activeProductSortList();
    if (!Array.isArray(activeProductSortSelectors)) {
      return [...catalogSortSelections];
    }

    const filteredActiveSortSelections = catalogSortSelections.filter((ss) =>
      activeProductSortSelectors.find((sort) => sort.sortIndex === ss.sortIndex)
    );

    if (!filteredActiveSortSelections.length) {
      return [...catalogSortSelections];
    }

    return filteredActiveSortSelections;
  };

  const ensureMappedDefaultIncluded = (selections) => {
    if (!mappedDefaultSelection) {
      return selections;
    }

    const withoutDuplicate = selections.filter(
      (ss) => ss.sortIndex !== mappedDefaultSelection.sortIndex
    );
    return [mappedDefaultSelection, ...withoutDuplicate];
  };

  const withDefaultFallback = (selections) => {
    if (mappedDefaultSelection) {
      return selections;
    }
    return [defaultSortFallback, ...selections];
  };

  const filterPriceSortSelections = (selections) => {
    if (!configStore.productList.priceSearchDisabled) {
      return selections;
    }

    const priceIndexes = [1, 2, 10];
    return selections.filter((ss) =>
      priceIndexes.every((priceIndex) => priceIndex !== ss.sortIndex)
    );
  };

  // Price filter first, then force-include the mapped default so a price-based
  // page/shop default (e.g. On Sale DISCOUNT) is not dropped from the menu.
  let activeSortSelections = filterActiveSortSelections();
  activeSortSelections = filterPriceSortSelections(activeSortSelections);
  activeSortSelections = ensureMappedDefaultIncluded(activeSortSelections);
  activeSortSelections = withDefaultFallback(activeSortSelections);

  const handleSelectSort = (sortKey, sortOrder) => {
    // Selecting the effective list default clears URL sort so BE applies its
    // default (same semantics as the old synthetic Default row).
    if (
      mappedDefaultSelection &&
      sortKey === mappedDefaultSelection.sortKey &&
      sortOrder === mappedDefaultSelection.sortOrder
    ) {
      onSelectSort(null, null);
      return;
    }
    onSelectSort(sortKey, sortOrder);
  };

  return (
    <SortSelection
      className={'ProductListSortSelection'}
      sortSelections={activeSortSelections}
      onSelectSort={handleSelectSort}
      selectedSortKey={selectedSortKey}
      selectedSortOrder={selectedSortOrder}
    />
  );
};

ProductListSortSelection.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  onSelectSort: PropTypes.func.isRequired,
  selectedSortKey: PropTypes.string,
  selectedSortOrder: PropTypes.string,
  listDefaultSortOption: PropTypes.string,
};

export default inject(
  'configStore',
  'productStore'
)(observer(ProductListSortSelection));
